import React, { useCallback, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { Wallet, ChevronDown, Unplug, LogOut, Smartphone, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '../lib/utils'
import { useTerms } from './TermsProvider'

/**
 * Props for the WalletConnectButton component
 * Allows customization of appearance and behavior
 */
interface WalletConnectButtonProps {
  /** Additional CSS classes */
  className?: string
  /** Custom text for the connect button (default: auto-detected based on device) */
  connectLabel?: string
  /** Whether to show the dropdown menu for connected state */
  showDropdown?: boolean
  /** Variant style: 'primary' | 'secondary' | 'ghost' */
  variant?: 'primary' | 'secondary' | 'ghost'
  /** Size: 'sm' | 'md' | 'lg' */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Detects whether the current browser is running on a mobile device.
 * Checks user agent string and touch capability for comprehensive detection.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const userAgent = navigator.userAgent || navigator.vendor || ''
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  return mobileRegex.test(userAgent) || (hasTouchScreen && window.innerWidth < 768)
}

/**
 * Checks whether the current page is being served over a secure context (HTTPS).
 * MWA protocol requires HTTPS — deep links silently fail on HTTP.
 */
function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false
  return window.isSecureContext === true
}

/**
 * WalletConnectButton Component
 *
 * A custom wallet connection button that intercepts clicks to show the
 * Terms of Service modal BEFORE opening the wallet selection modal.
 * This provides a better UX by:
 * 1. Checking if TOS is already accepted
 * 2. If not: Shows TOS modal first, then opens wallet modal after acceptance
 * 3. If accepted: Opens wallet modal directly
 *
 * Mobile Support:
 * - Detects mobile devices and shows appropriate connect label
 * - Compact layout on small screens (shorter label, smaller padding)
 * - Works with SolanaMobileWalletAdapter for deep-link based connections
 * - On mobile, the wallet modal triggers the MWA protocol to open wallet apps
 * - Warns when HTTPS is not available (required for MWA)
 *
 * Features:
 * - ADA/WCAG 2.1 compliant with proper ARIA attributes
 * - Keyboard navigation support
 * - Multiple visual variants
 * - Shows connected wallet info when connected
 * - Disconnect functionality
 * - Mobile-aware connect label and icon
 * - HTTPS detection with user warning
 * - Minimum 44x44px touch target for mobile (WCAG 2.5.5)
 */
export function WalletConnectButton({
  className,
  connectLabel,
  showDropdown = true,
  variant = 'primary',
  size = 'md'
}: WalletConnectButtonProps): JSX.Element {
  // Wallet adapter hooks
  const { connected, publicKey, disconnect, wallet } = useWallet()
  const { setVisible: setWalletModalVisible } = useWalletModal()

  // Terms of Service context
  const { hasAcceptedTerms, promptTermsAcceptance, setOnTermsAccepted } = useTerms()

  // Dropdown state for connected wallet
  const [dropdownOpen, setDropdownOpen] = React.useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Detect mobile device for UI adjustments (memoized to avoid recalculating)
  const isMobile = useMemo(() => isMobileDevice(), [])

  // Check if we're on a secure context (HTTPS) — needed for mobile wallet deep links
  const isSecure = useMemo(() => isSecureContext(), [])

  // Determine the appropriate connect label based on device type
  // Mobile: shorter "Connect" label to save space in the header
  const resolvedLabel = connectLabel ?? (isMobile ? 'Connect' : 'Select Wallet')

  /**
   * Opens the wallet modal programmatically.
   * Called either directly (if TOS accepted) or as callback after TOS acceptance.
   * On mobile, the wallet modal will show the MWA adapter which triggers deep links.
   *
   * Before opening, checks if we're on mobile without HTTPS and warns the user
   * since MWA requires a secure context.
   */
  const openWalletModal = useCallback(() => {
    // Warn mobile users if not on HTTPS — MWA deep links won't work
    if (isMobile && !isSecure) {
      console.warn(
        '[WalletConnectButton] ⚠️ Mobile wallet connections require HTTPS. ' +
        'Current context is not secure — MWA deep links may fail.'
      )
      toast.error(
        '🔒 HTTPS Required — Mobile wallet connections require a secure (HTTPS) connection. ' +
        'Wallet deep links may not work over HTTP.',
        { duration: 6000, id: 'https-warning' }
      )
    }
    setWalletModalVisible(true)
  }, [setWalletModalVisible, isMobile, isSecure])

  /**
   * Handle click on the Connect/Select Wallet button.
   * Intercepts the click to check TOS acceptance first.
   */
  const handleConnectClick = useCallback(() => {
    console.log('[WalletConnectButton] handleConnectClick', {
      hasAcceptedTerms,
      isMobile,
      isSecure,
      protocol: typeof window !== 'undefined' ? window.location.protocol : 'N/A',
    })

    if (hasAcceptedTerms) {
      // TOS already accepted — open wallet modal directly
      console.log('[WalletConnectButton] TOS already accepted, opening wallet modal directly')
      openWalletModal()
    } else {
      // TOS not accepted — show TOS modal first
      // Set callback to open wallet modal after terms acceptance
      console.log('[WalletConnectButton] TOS not accepted, showing TOS modal first')
      setOnTermsAccepted(openWalletModal)
      promptTermsAcceptance()
    }
  }, [hasAcceptedTerms, isMobile, isSecure, openWalletModal, promptTermsAcceptance, setOnTermsAccepted])

  /**
   * Handle disconnect click.
   * Disconnects the wallet and closes dropdown.
   */
  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect()
      setDropdownOpen(false)
    } catch (error) {
      console.error('Error disconnecting wallet:', error)
    }
  }, [disconnect])

  /**
   * Handle click outside dropdown to close it.
   */
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [dropdownOpen])

  /**
   * Handle keyboard navigation for dropdown.
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setDropdownOpen(false)
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (connected) {
        setDropdownOpen(!dropdownOpen)
      } else {
        handleConnectClick()
      }
    }
  }

  /**
   * Format wallet address for display (truncated).
   */
  const formatAddress = (address: string): string => {
    if (!address || address.length < 8) return address
    return `${address.slice(0, 4)}...${address.slice(-4)}`
  }

  /**
   * Get button size classes based on size prop.
   * Uses responsive padding for mobile-friendly touch targets.
   */
  const getSizeClasses = (): string => {
    switch (size) {
      case 'sm':
        return 'px-2.5 py-2 text-xs sm:text-sm'
      case 'lg':
        return 'px-6 py-4 text-lg'
      default:
        // Compact padding on mobile, standard on desktop
        return 'px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm'
    }
  }

  /**
   * Get button variant classes based on variant prop.
   */
  const getVariantClasses = (): string => {
    switch (variant) {
      case 'secondary':
        return cn(
          'glass-dark border-dark-border text-text-secondary',
          'hover:bg-dark-panel/50 hover:text-cyber-cyan hover:border-cyber-cyan/30',
          'focus:ring-cyber-cyan/40'
        )
      case 'ghost':
        return cn(
          'bg-transparent border-transparent text-text-secondary',
          'hover:bg-dark-panel/30 hover:text-cyber-cyan',
          'focus:ring-cyber-cyan/30'
        )
      default:
        return cn(
          'glass-dark border-cyber-cyan/30 text-cyber-cyan',
          'hover:bg-cyber-cyan/10 hover:border-cyber-cyan hover:shadow-neon-cyan',
          'focus:ring-cyber-cyan/40'
        )
    }
  }

  // Base button classes with mobile-friendly minimum touch target
  const baseClasses = cn(
    'rounded-cyber font-medium transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-bg',
    'border',
    // WCAG 2.5.5: Minimum 44x44px touch target for mobile
    'min-h-[44px]',
    getSizeClasses(),
    getVariantClasses(),
    className
  )

  // Connected state — show wallet info with optional dropdown
  if (connected && publicKey) {
    const walletAddress = publicKey.toBase58()
    const walletName = wallet?.adapter?.name || 'Wallet'

    if (showDropdown) {
      return (
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            onKeyDown={handleKeyDown}
            className={cn(baseClasses, 'flex items-center space-x-1.5 sm:space-x-2')}
            aria-expanded={dropdownOpen}
            aria-haspopup="menu"
            aria-label={`Connected wallet: ${formatAddress(walletAddress)}. Click to open menu.`}
          >
            {/* Wallet icon or adapter icon */}
            {wallet?.adapter?.icon ? (
              <img
                src={wallet.adapter.icon}
                alt={walletName}
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
            ) : (
              <Wallet className="w-4 h-4" aria-hidden="true" />
            )}

            <span className="font-mono">{formatAddress(walletAddress)}</span>

            <ChevronDown
              className={cn(
                'w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200',
                dropdownOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>

          {/* Dropdown menu - positioned above mobile menu backdrop
           * z-[200] ensures it's above the header content
           */}
          {dropdownOpen && (
            <div
              className={cn(
                'absolute right-0 mt-2 w-52 sm:w-56 z-[200]',
                'border border-cyber-cyan/20 rounded-cyber shadow-glass-lg',
                'animate-scale-in origin-top-right'
              )}
              style={{
                background: 'rgba(12, 16, 28, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
              role="menu"
              aria-label="Wallet options"
            >
              {/* Wallet info header */}
              <div className="p-3 border-b border-dark-border/50">
                <div className="flex items-center space-x-3">
                  {wallet?.adapter?.icon ? (
                    <img
                      src={wallet.adapter.icon}
                      alt={walletName}
                      className="w-8 h-8"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-cyber-gradient rounded-full flex items-center justify-center">
                      <Wallet className="w-4 h-4 text-dark-bg" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {walletName}
                    </div>
                    <div className="text-xs text-text-muted font-mono truncate">
                      {formatAddress(walletAddress)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu options */}
              <div className="py-1">
                {/* Change Wallet */}
                <button
                  onClick={() => {
                    setDropdownOpen(false)
                    openWalletModal()
                  }}
                  className={cn(
                    'w-full px-4 py-2.5 text-left text-sm',
                    'flex items-center space-x-2',
                    'text-text-secondary hover:text-cyber-cyan hover:bg-dark-panel/50',
                    'transition-colors duration-200',
                    // Minimum touch target height for mobile
                    'min-h-[44px]'
                  )}
                  role="menuitem"
                >
                  <Unplug className="w-4 h-4" aria-hidden="true" />
                  <span>Change Wallet</span>
                </button>

                {/* Disconnect */}
                <button
                  onClick={handleDisconnect}
                  className={cn(
                    'w-full px-4 py-2.5 text-left text-sm',
                    'flex items-center space-x-2',
                    'text-text-secondary hover:text-status-error hover:bg-status-error/10',
                    'transition-colors duration-200',
                    // Minimum touch target height for mobile
                    'min-h-[44px]'
                  )}
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                  <span>Disconnect</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )
    }

    // Simple connected state without dropdown
    return (
      <button
        type="button"
        onClick={handleDisconnect}
        onKeyDown={handleKeyDown}
        className={cn(baseClasses, 'flex items-center space-x-2')}
        aria-label={`Disconnect wallet ${formatAddress(walletAddress)}`}
      >
        {wallet?.adapter?.icon ? (
          <img
            src={wallet.adapter.icon}
            alt={walletName}
            className="w-4 h-4 sm:w-5 sm:h-5"
          />
        ) : (
          <Wallet className="w-4 h-4" aria-hidden="true" />
        )}
        <span className="font-mono">{formatAddress(walletAddress)}</span>
      </button>
    )
  }

  // Disconnected state — show connect button with mobile-aware icon
  // If on mobile without HTTPS, show a warning indicator
  return (
    <button
      type="button"
      onClick={handleConnectClick}
      onKeyDown={handleKeyDown}
      className={cn(baseClasses, 'flex items-center space-x-1.5 sm:space-x-2 whitespace-nowrap')}
      aria-label={`${resolvedLabel}. ${!hasAcceptedTerms ? 'You will first need to accept the Terms of Service.' : ''}${isMobile && !isSecure ? ' Warning: HTTPS required for mobile wallet connections.' : ''}`}
    >
      {/* Show smartphone icon on mobile, wallet icon on desktop */}
      {/* Show warning triangle if mobile + no HTTPS */}
      {isMobile && !isSecure ? (
        <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" aria-hidden="true" />
      ) : isMobile ? (
        <Smartphone className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      ) : (
        <Wallet className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      )}
      <span>{resolvedLabel}</span>
    </button>
  )
}

export default WalletConnectButton
